import { AUDIO } from '../config/tuning';

/**
 * The maths behind 3D audio: distance, delay, Doppler, occlusion (SPEC §3.11).
 *
 * Pure functions, no Web Audio types, so the parts that decide *what a player hears* can be tested
 * without a browser. That matters more here than usual: audio bugs are hard to see in a test and
 * easy to mistake for taste, so the physical parts are pinned numerically and only the timbre is
 * left to the ear.
 *
 * Distances in metres, speeds m/s, times seconds, frequencies hertz.
 */

/**
 * Delay before a distant event is heard, seconds.
 *
 * The M9 note calls this out specifically: *"seeing the flash before hearing the blast is a detail
 * players consciously notice"*. At 2 km that is nearly six seconds — long enough that scheduling
 * it correctly is the difference between an explosion and two unrelated events.
 */
export function propagationDelayS(distanceM: number): number {
  return Math.max(0, distanceM) / AUDIO.speedOfSoundMs;
}

/**
 * Distance attenuation, 0–1.
 *
 * Inverse falloff with a reference distance rather than true inverse-square: real inverse-square
 * makes everything beyond a few hundred metres inaudible, and this is a game in which the
 * interesting events happen kilometres away. The reference distance is the knob that keeps a
 * launch at 4 km present without making one at 100 m deafening.
 */
export function distanceGain(distanceM: number): number {
  if (distanceM >= AUDIO.maxAudibleM) return 0;
  const d = Math.max(distanceM, 1);
  const gain = AUDIO.referenceDistanceM / (AUDIO.referenceDistanceM + d);
  // Fade the last decade to zero so a source does not pop out at the audible limit.
  const fade = 1 - d / AUDIO.maxAudibleM;
  return gain * clamp01(fade);
}

/**
 * Doppler frequency ratio for a source and listener along the line between them.
 *
 * `closingMs` is positive when the gap is shrinking. Clamped away from the speed of sound because
 * the classical formula diverges there, and a missile flyby genuinely gets close enough to the
 * numbers for it to matter.
 */
export function dopplerRatio(closingMs: number): number {
  const c = AUDIO.speedOfSoundMs;
  const closing = Math.max(-c * 0.9, Math.min(c * 0.9, closingMs));
  return c / (c - closing);
}

/** Gain multiplier when terrain blocks the line of sight. */
export function occlusionGain(blocked: boolean): number {
  return blocked ? AUDIO.occlusionGain : 1;
}

/** Low-pass cutoff for a source, Hz. Rock eats treble long before it eats level. */
export function occlusionCutoffHz(blocked: boolean): number {
  return blocked ? AUDIO.occlusionCutoffHz : 20000;
}

/**
 * Engine fundamental, Hz, from throttle and airspeed.
 *
 * Both terms are needed. Throttle alone gives a note that does not change in a dive; airspeed
 * alone gives one that ignores the player's hand. The combination is what makes the engine read as
 * an engine rather than a slider.
 */
export function engineFundamentalHz(throttle: number, airspeedMs: number): number {
  const t = clamp01(throttle);
  const base = AUDIO.engineBaseHz * (1 + (AUDIO.enginePitchRange - 1) * t);
  return base * (1 + AUDIO.enginePitchPerMs * Math.max(0, airspeedMs));
}

/** Airflow noise cutoff, Hz, rising with airspeed. */
export function airflowCutoffHz(airspeedMs: number): number {
  const t = clamp01(Math.max(0, airspeedMs) / 140);
  return AUDIO.airflowCutoffMinHz + (AUDIO.airflowCutoffMaxHz - AUDIO.airflowCutoffMinHz) * t;
}

/** Airflow level, 0–1, from airspeed. Quadratic, because so is dynamic pressure. */
export function airflowGain(airspeedMs: number): number {
  const t = clamp01(Math.max(0, airspeedMs) / 140);
  return t * t;
}

/**
 * Airframe stress level, 0–1, above the threshold g.
 *
 * Zero below the threshold rather than a faint constant creak: a sound that is always present
 * carries no information, and this one exists to tell the player they are near the limit.
 */
export function stressGain(loadFactor: number): number {
  const over = Math.abs(loadFactor) - AUDIO.stressGThreshold;
  if (over <= 0) return 0;
  return clamp01(over / (8 - AUDIO.stressGThreshold));
}

/** RWR pulse interval, seconds, for a contact state. Launch is continuous, hence zero. */
export function rwrIntervalS(state: 'search' | 'track' | 'launch'): number {
  if (state === 'search') return AUDIO.rwrSearchIntervalS;
  if (state === 'track') return AUDIO.rwrTrackIntervalS;
  return 0;
}

/** RWR tone frequency, Hz, for a contact state. */
export function rwrToneHz(state: 'search' | 'track' | 'launch'): number {
  if (state === 'search') return AUDIO.rwrSearchHz;
  if (state === 'track') return AUDIO.rwrTrackHz;
  return AUDIO.rwrLaunchHz;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
